import { Fragment, PropsWithChildren, useMemo, useRef } from 'react';
import { isEqual } from 'lodash';
import { Box, Grid } from '@mui/material';
import { LayoutComponentMap, LayoutComponentNames } from '@diana/core';
import {
  useDropLayoutComponent,
  IDndLayoutComponentLocation,
} from '../../../../../providers/DndLayoutComponents2';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { DropHereBox } from '../../../Components/DropHereBox/DropHereBox';
import { useDndComponentHoverEvent } from './useDndComponentHoverEvent';
import { isSectionComponent } from '../../../Support';

export interface IComponentBuilderDropPlaceholderProps {
  accept: string | symbol;
  location: IDndLayoutComponentLocation;

  layoutId: string;
  taskId: string | null;
  stepId: string | null;
  flowId: string | null;
}

/**
 * BuilderComponentDropPlaceholder is a placeholder for new components to drop on.
 */
export const ComponentBuilderDropPlaceholder = (props: IComponentBuilderDropPlaceholderProps) => {
  const { location } = props;

  const dragItem = useFlowBuilder2PageStore((s) => s.layoutDndDragItem);
  const isActive = useFlowBuilder2PageStore((s) =>
    isEqual(s.layoutDndPlaceholderLocation, location),
  );

  const componentSize = useMemo(() => {
    const width = dragItem?.component.properties.width ?? 'col-12';
    return Number(width.replace(/^col-/, ''));
  }, [dragItem?.component.properties.width]);

  if (!dragItem || !isActive) return <Fragment />;

  const Component =
    LayoutComponentMap[dragItem.component.component] ||
    LayoutComponentMap[LayoutComponentNames.MISSING_COMPONENT];

  return (
    <Content
      {...props}
      componentSize={componentSize}
      minHeight={isSectionComponent(dragItem.component) ? '50px' : '78px'}
    >
      <Component
        step={{} as any}
        workId={''}
        data={{}}
        layoutComponent={dragItem.component}
        isWorkEditable={false}
        isWorkflowEditMode={true}
      />
    </Content>
  );
};

const Content = (
  props: PropsWithChildren<
    IComponentBuilderDropPlaceholderProps & { componentSize: number; minHeight: string }
  >,
) => {
  const { accept, location, componentSize, minHeight, children, layoutId, taskId, stepId, flowId } =
    props;

  const dropRef = useRef<HTMLDivElement>(null);

  const { onDndComponentHover } = useDndComponentHoverEvent({ layoutId, taskId, stepId, flowId });

  const [{ isOver }, drop] = useDropLayoutComponent<{ isOver: boolean }>(
    {
      accept,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (dragObject, monitor) => {
        return onDndComponentHover({
          monitor,
          dragObject,
          targetElement: dropRef.current,
          targetDropResult: {
            componentId: null,
            componentSize,
            location: {
              layoutId: location.layoutId,
              parentId: location.parentId,
              index: location.index,
            },
          },
        });
      },
    },
    [
      accept,
      componentSize,
      location.layoutId,
      location.parentId,
      location.index,
      onDndComponentHover,
    ],
  );

  drop(dropRef);

  return (
    <Grid item xs={12} md={componentSize}>
      <Box
        ref={dropRef}
        sx={{
          height: '100%',
          minHeight,
          position: 'relative',
        }}
      >
        <Box sx={{ opacity: 0, py: 1 }}>{children}</Box>
        <DropHereBox
          isOver={isOver}
          sx={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            left: 0,
            top: 0,
          }}
        />
      </Box>
    </Grid>
  );
};
